import { useState } from 'react';
import {
  CheckCircle2,
  ClipboardCheck,
  FileWarning,
  FileX,
  Gavel,
  PartyPopper,
  RefreshCw,
  Repeat,
  ScrollText,
  Wallet,
} from 'lucide-react';

import { Button } from '@/ui/base/button';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { formatarDinheiro } from '@/nucleo/dinheiro';
import { cn } from '@/nucleo/utilitarios';

import { CartaoIndicador } from './CartaoIndicador';
import { CadinDevedoresModal } from './CadinDevedoresModal';
import type { OperacaoImediata as DadosOperacaoImediata, ReservaSof } from './tipos';

/**
 * Seção "Operação imediata" — ações pendentes que demandam atenção agora.
 *
 * O botão "Atualizar CADIN" não chama serviço nenhum (não há API/banco
 * ainda — ver a exceção documentada em `../../_dev/cenarioDeTela.ts`): ele
 * apenas simula a espera de uma consulta e confirma com um aviso na própria
 * página, que não some como um aviso curto.
 */
export function OperacaoImediata({
  dados,
  reservaSof,
}: {
  dados: DadosOperacaoImediata;
  reservaSof: ReservaSof;
}) {
  const [modalCadinAberto, setModalCadinAberto] = useState(false);
  const [atualizandoCadin, setAtualizandoCadin] = useState(false);
  const [avisoCadin, setAvisoCadin] = useState<string | null>(null);

  const handleAtualizarCadin = () => {
    setAtualizandoCadin(true);
    setAvisoCadin(null);
    window.setTimeout(() => {
      setAtualizandoCadin(false);
      setAvisoCadin(
        `Consulta CADIN concluída · ${dados.cadin.totalPendentes} credenciado(s) consultado(s), ${dados.cadin.totalPf + dados.cadin.totalPj} com pendência.`,
      );
    }, 900);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-foreground/90">
            Operação imediata
          </h2>
          <p className="text-xs text-muted-foreground">
            Ações pendentes que demandam atenção do gestor agora
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAtualizarCadin}
          disabled={atualizandoCadin}
          className="mb-1 gap-2"
        >
          <RefreshCw
            className={cn('h-3.5 w-3.5', atualizandoCadin && 'animate-spin')}
            aria-hidden
          />
          Atualizar CADIN
        </Button>
      </div>

      {avisoCadin && (
        <div role="status" aria-live="polite">
          <Alert className="border-success/50 bg-success/10 text-success">
            <CheckCircle2 className="text-success" aria-hidden />
            <AlertDescription className="text-success">{avisoCadin}</AlertDescription>
          </Alert>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <CartaoIndicador
          titulo="A pagar"
          valor={formatarDinheiro(dados.aPagar.valor)}
          descricao={`${dados.aPagar.qtdDemonstrativos} demonstrativo(s)`}
          icone={Wallet}
          tom={dados.aPagar.qtdDemonstrativos > 0 ? 'atencao' : 'positivo'}
          to="/operacao/financeiro/demonstrativos"
        />
        <CartaoIndicador
          titulo="Conciliação pendente"
          valor={dados.conciliacaoPendente}
          descricao="Pagos sem confirmação no SOF"
          icone={Wallet}
          tom={dados.conciliacaoPendente > 0 ? 'atencao' : 'positivo'}
          to="/operacao/financeiro/sof/status"
        />
        <CartaoIndicador
          titulo="DRE sem apontamento"
          valor={dados.dreSemApontamento}
          descricao="No mês de competência atual"
          icone={FileWarning}
          tom={dados.dreSemApontamento > 0 ? 'atencao' : 'positivo'}
          to="/sme/cobertura"
        />
        <CartaoIndicador
          titulo="Apontamentos recebidos"
          valor={dados.apontamentosRecebidos}
          descricao="Lançamentos diários no mês"
          icone={ClipboardCheck}
          to="/operacao/financeiro/apontamentos"
        />
        <CartaoIndicador
          titulo="Revisões/ajustes abertos"
          valor={dados.revisoesAbertas}
          descricao="Pendente · Em análise · Encaminhada"
          icone={Repeat}
          tom={dados.revisoesAbertas > 0 ? 'atencao' : 'positivo'}
          to="/operacao/financeiro/solicitacoes-sme"
        />
        <CartaoIndicador
          titulo="Multas identificadas"
          valor={dados.multasIdentificadas}
          descricao="No mês de competência"
          icone={Gavel}
          tom={dados.multasIdentificadas > 0 ? 'atencao' : 'neutro'}
          to="/operacao/penalidades"
        />
        <CartaoIndicador
          titulo="Eventos ativos"
          valor={dados.eventosAtivos}
          descricao="OS de tipo Evento em andamento"
          icone={PartyPopper}
          to="/operacao/ordens-de-servico"
        />
        <CartaoIndicador
          titulo="FACC ausente"
          valor={dados.faccAusente}
          descricao="Credenciados ativos sem ficha atualizada"
          icone={FileX}
          tom={dados.faccAusente > 0 ? 'critico' : 'positivo'}
          to="/operacao/credenciados"
        />
        <CartaoIndicador
          titulo="Pendências CADIN"
          valor={dados.cadin.totalPendentes}
          descricao={
            dados.cadin.totalPendentes > 0
              ? `${dados.cadin.totalPf} CPF · ${dados.cadin.totalPj} CNPJ · ver lista`
              : 'Sem pendências registradas'
          }
          icone={ScrollText}
          tom={dados.cadin.totalPendentes > 0 ? 'critico' : 'positivo'}
          aoClicar={() => setModalCadinAberto(true)}
        />
        <CartaoIndicador
          titulo="Saldo de reserva SOF"
          valor={formatarDinheiro(reservaSof.totalReserva)}
          descricao={`${reservaSof.qtdReservas} dotação(ões) · simulado`}
          icone={Wallet}
          tom={reservaSof.totalReserva > 0 ? 'positivo' : 'atencao'}
          to="/operacao/financeiro/sof/reservas"
        />
      </div>

      <CadinDevedoresModal
        open={modalCadinAberto}
        onOpenChange={setModalCadinAberto}
        dados={dados.cadin}
      />
    </div>
  );
}
